import type { Auth, User } from "firebase/auth";
import { memberName } from "./memberNames";
import { signOut } from "./signOut";
import { type FormEvent, useEffect, useRef, useState } from "react";
import * as api from "./api";
import { callDay, callTime, emailName, shortTime } from "./calls";
import { go, useTitle } from "./hooks";
import { dayText } from "./accounts";
import {
  byUrgency,
  customerAckText,
  daysOpen,
  daysOpenText,
  defaultView,
  historyText,
  type IssueField,
  type IssueReason,
  type IssueView,
  isOverdue,
  issueField,
  issueTitle,
  nextSteps,
  openError,
  REASON_TEXT,
  REASONS,
  RESOLUTION_TEXT,
  RESOLUTIONS,
  type Resolution,
  resolveError,
  STATUS_TEXT,
  STEP_TEXT,
  type StepKind,
  stepDoneText,
} from "./issues";
import { todayIn } from "./pickup";
import { type Saved, SERVER_PROBLEM, saveFailure } from "./save";
import { ISSUE_COLUMNS } from "./columns";
import { sortRows } from "./sort";
import { SortSelect, SortTh, useSort } from "./SortHeader";
import { Skeleton } from "./Skeleton";

function SessionButton({ saved, auth }: { saved: Saved | null; auth: Auth }) {
  if (!saved || saved.ok || !saved.session) return null;
  return (
    <>
      {" "}
      <button type="button" onClick={() => signOut(auth)}>
        Sign in again
      </button>
    </>
  );
}

function useAssignees(user: User, accountId: string) {
  const [found, setFound] = useState<api.Assignee[] | "failed" | undefined>(undefined);
  useEffect(() => {
    let live = true;
    user
      .getIdToken()
      .then((token) => api.assignees(token, accountId))
      .then((list) => live && setFound(list))
      .catch(() => live && setFound("failed"));
    return () => {
      live = false;
    };
  }, [user, accountId]);
  return found;
}

function AssigneeSelect({
  id,
  found,
  value,
  onChange,
  invalid,
}: {
  id: string;
  found: api.Assignee[] | "failed" | undefined;
  value: string;
  onChange: (value: string) => void;
  invalid: boolean;
}) {
  return (
    <select
      id={id}
      data-testid={id}
      value={value}
      disabled={!Array.isArray(found)}
      onChange={(e) => onChange(e.target.value)}
      aria-invalid={invalid || undefined}
    >
      <option value="">{found === "failed" ? "Cannot load the members" : found ? "Choose a member" : "Loading…"}</option>
      {Array.isArray(found) &&
        found.map((person) => (
          <option key={person.id} value={person.id} data-email={person.email}>
            {memberName(person.email)}
          </option>
        ))}
    </select>
  );
}

export function IssueForm({
  account,
  org,
  auth,
  user,
  quiet,
  fromCall,
  onSaved,
}: {
  account: api.AccountDetail;
  org: api.Bootstrap;
  auth: Auth;
  user: User;
  quiet: boolean;
  fromCall: string | null;
  onSaved: (next: api.AccountDetail) => void;
}) {
  const people = useAssignees(user, account.id);
  const [reason, setReason] = useState<IssueReason>("service");
  const [note, setNote] = useState("");
  useEffect(() => {
    if (fromCall) setNote(fromCall);
  }, [fromCall]);
  const [assignee, setAssignee] = useState("");
  const [due, setDue] = useState("");
  const [saved, setSaved] = useState<Saved | null>(null);
  const [saving, setSaving] = useState(false);
  const entryId = useRef<string | null>(null);
  const today = todayIn(org.time_zone);
  const edit = (next: () => void) => {
    next();
    entryId.current = null;
    setSaved(null);
  };
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const problem = openError(assignee, note, due, today);
    if (problem) {
      setSaved({ ok: false, field: true, text: problem });
      return;
    }
    entryId.current ??= crypto.randomUUID();
    setSaving(true);
    setSaved(null);
    try {
      const entry: api.IssueEntry = {
        id: entryId.current,
        reason,
        source: fromCall ? "call" : "screen",
        note: note.trim(),
        assignee_id: assignee,
        due_date: due || null,
      };
      const next = await api.openIssue(await user.getIdToken(), account.id, entry);
      entryId.current = null;
      setNote("");
      setDue("");
      setAssignee("");
      onSaved(next);
      const picked = Array.isArray(people) ? people.find((p) => p.id === entry.assignee_id)?.email : undefined;
      const who = picked ? memberName(picked) : undefined;
      setSaved({ ok: true, text: `Opened: ${REASON_TEXT[entry.reason]}${who ? `, assigned to ${who}` : ""}.` });
    } catch (e) {
      const failure = saveFailure(e, "The issue");
      const status = e instanceof api.ApiError ? e.status : 0;
      setSaved(!failure.ok && status !== 422 ? { ...failure, field: false } : failure);
    } finally {
      setSaving(false);
    }
  };
  const fieldError = saved && !saved.ok && saved.field ? saved.text : null;
  const errorAt = (field: IssueField) =>
    fieldError && issueField(fieldError) === field ? (
      <p id={`issue-${field}-error`} role="alert" className="error" data-testid="issue-error">
        {fieldError}
      </p>
    ) : null;
  return (
    <form onSubmit={submit} noValidate data-testid="issue-form" aria-labelledby="issue-form-title">
      <h3 id="issue-form-title">{fromCall ? "Open an issue from the call" : "Open an issue"}</h3>
      <label htmlFor="issue-reason">Reason</label>
      <select id="issue-reason" data-testid="issue-reason" value={reason} onChange={(e) => edit(() => setReason(e.target.value as IssueReason))}>
        {REASONS.map((value) => (
          <option key={value} value={value}>
            {REASON_TEXT[value]}
          </option>
        ))}
      </select>
      <label htmlFor="issue-assignee">Assignee</label>
      <AssigneeSelect
        id="issue-assignee"
        found={people}
        value={assignee}
        onChange={(v) => edit(() => setAssignee(v))}
        invalid={Boolean(fieldError) && issueField(fieldError ?? "") === "assignee"}
      />
      {errorAt("assignee")}
      <label htmlFor="issue-note">Note</label>
      <textarea
        id="issue-note"
        data-testid="issue-note"
        rows={2}
        value={note}
        onChange={(e) => edit(() => setNote(e.target.value))}
        aria-invalid={errorAt("note") ? true : undefined}
        aria-describedby={errorAt("note") ? "issue-note-error" : undefined}
      />
      {errorAt("note")}
      <label htmlFor="issue-due">Due date (optional)</label>
      <input
        id="issue-due"
        data-testid="issue-due"
        type="date"
        min={today}
        value={due}
        onChange={(e) => edit(() => setDue(e.target.value))}
        aria-invalid={errorAt("due") ? true : undefined}
        aria-describedby={errorAt("due") ? "issue-due-error" : undefined}
      />
      {errorAt("due")}
      <div className="actions">
        <button className={quiet ? undefined : "primary"} data-testid="save-issue" disabled={saving}>
          {saving ? "Saving…" : "Open the issue"}
        </button>
      </div>
      <p role="status" data-testid="issue-status" className={saved && !saved.ok && !saved.field ? "error" : undefined}>
        {saved && !fieldError && saved.text}
        <SessionButton saved={saved} auth={auth} />
      </p>
    </form>
  );
}

function StepForm({
  issue,
  kind,
  auth,
  user,
  onDone,
  onCancel,
}: {
  issue: api.Issue;
  kind: StepKind;
  auth: Auth;
  user: User;
  onDone: (text: string) => void;
  onCancel: () => void;
}) {
  const people = useAssignees(user, issue.account_id);
  const [resolution, setResolution] = useState<Resolution | "">("");
  const [assignee, setAssignee] = useState("");
  const [note, setNote] = useState("");
  const [saved, setSaved] = useState<Saved | null>(null);
  const [saving, setSaving] = useState(false);
  const stepId = useRef<string | null>(null);
  const choice = useRef<HTMLSelectElement>(null);
  const noteRef = useRef<HTMLTextAreaElement>(null);
  useEffect(() => (kind === "resolve" ? choice.current : noteRef.current)?.focus(), [kind]);
  const edit = (next: () => void) => {
    next();
    stepId.current = null;
    setSaved(null);
  };
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const problem =
      kind === "resolve" ? resolveError(resolution, note) : kind === "reassign" && !assignee ? "Choose the new assignee." : "";
    if (problem) {
      setSaved({ ok: false, field: true, text: problem });
      const field = issueField(problem) === "note" ? noteRef.current : (choice.current ?? document.getElementById(`step-${issue.id}-assignee`));
      field?.focus();
      return;
    }
    stepId.current ??= crypto.randomUUID();
    setSaving(true);
    setSaved(null);
    try {
      const step: api.IssueStep = {
        id: stepId.current,
        kind,
        note: note.trim(),
        ...(kind === "resolve" && resolution ? { resolution } : {}),
        ...(kind === "reassign" ? { assignee_id: assignee } : {}),
      };
      await api.stepIssue(await user.getIdToken(), issue.id, step);
      stepId.current = null;
      const picked = Array.isArray(people) ? people.find((p) => p.id === assignee)?.email : undefined;
      const who = picked ? memberName(picked) : undefined;
      onDone(stepDoneText(kind, issue, who));
    } catch (e) {
      setSaved(saveFailure(e, "The step"));
    } finally {
      setSaving(false);
    }
  };
  const fieldError = saved && !saved.ok && saved.field ? saved.text : null;
  const base = `step-${issue.id}`;
  const errorOn = fieldError ? issueField(fieldError) : null;
  const describe = (field: IssueField) =>
    errorOn === field ? { "aria-invalid": true, "aria-describedby": `${base}-error` } : {};
  return (
    <form onSubmit={submit} noValidate className="step-form" data-testid="issue-step-form">
      {kind === "resolve" && (
        <>
          <label htmlFor={`${base}-resolution`}>Resolution</label>
          <select
            ref={choice}
            id={`${base}-resolution`}
            data-testid="issue-resolution"
            value={resolution}
            onChange={(e) => edit(() => setResolution(e.target.value as Resolution))}
            {...describe("resolution")}
          >
            <option value="">Choose a resolution</option>
            {RESOLUTIONS.map((value) => (
              <option key={value} value={value}>
                {RESOLUTION_TEXT[value]}
              </option>
            ))}
          </select>
        </>
      )}
      {kind === "reassign" && (
        <>
          <label htmlFor={`${base}-assignee`}>New assignee</label>
          <AssigneeSelect id={`${base}-assignee`} found={people} value={assignee} onChange={(v) => edit(() => setAssignee(v))} invalid={errorOn === "assignee"} />
        </>
      )}
      <label htmlFor={`${base}-note`}>Note{kind === "resolve" ? "" : " (optional)"}</label>
      <textarea
        ref={noteRef}
        id={`${base}-note`}
        data-testid="issue-step-note"
        rows={2}
        value={note}
        onChange={(e) => edit(() => setNote(e.target.value))}
        {...describe("note")}
      />
      {fieldError && (
        <p id={`${base}-error`} role="alert" className="error" data-testid="issue-step-error">
          {fieldError}
        </p>
      )}
      <div className="actions">
        <button className="primary" data-testid="save-step" disabled={saving}>
          {saving ? "Saving…" : STEP_TEXT[kind]}
        </button>
        <button type="button" data-testid="cancel-step" onClick={onCancel}>
          Cancel
        </button>
      </div>
      {saved && !fieldError && (
        <p role="alert" className="error">
          {saved.text}
          <SessionButton saved={saved} auth={auth} />
        </p>
      )}
    </form>
  );
}

function IssueHistoryList({ issue, org }: { issue: api.Issue; org: api.Bootstrap }) {
  if (issue.history.length < 2 && !issue.history.some((event) => event.note && event.kind !== "open")) return null;
  return (
    <details className="issue-history" data-testid="issue-history" open={issue.status === "resolved" || issue.status === "closed"}>
      <summary>History ({issue.history.length} steps)</summary>
      <ol>
        {issue.history.map((event, index) => (
          <li key={index} data-testid="issue-history-row">
            {historyText(event)} · {event.by_email ? memberName(event.by_email) : event.kind === "open" && issue.rule ? "the sensor rules" : "a removed member"} ·{" "}
            {callTime(event.at, org.time_zone)}
            {event.note && event.kind !== "open" && <span className="sub"> · {event.note}</span>}
          </li>
        ))}
      </ol>
    </details>
  );
}

export function IssueCard({
  issue,
  org,
  auth,
  user,
  showAccount,
  span,
  onChanged,
}: {
  issue: api.Issue;
  org: api.Bootstrap;
  auth: Auth;
  user: User;
  showAccount: string | null;
  span: number;
  onChanged: (text: string, kind: StepKind) => void;
}) {
  const [step, setStep] = useState<StepKind | null>(null);
  const steps = nextSteps(issue.status, issue.can_edit, issue.mine);
  const days = daysOpen(issue.opened_at, new Date());
  const late = isOverdue(issue, todayIn(org.time_zone));
  const resolved = [...issue.history].reverse().find((event) => event.kind === "resolve");
  return (
    <tbody data-testid="issue-row" data-day={callDay(issue.opened_at, org.time_zone)} className={`item issue-${issue.status}`}>
      <tr>
        <th scope="row" className="wrap">
          {issueTitle(issue)}
          {issue.note && (
            <span className="sub issue-note">{issue.note}</span>
          )}
          <IssueHistoryList issue={issue} org={org} />
        </th>
        <td data-label="State" className="state">
          <span data-testid="issue-state">{STATUS_TEXT[issue.status]}</span>
          {late && (
            <>
              {" "}
              <span className="chip-flag serious" data-testid="issue-overdue">
                Overdue
              </span>
            </>
          )}
          {issue.resolution && (
            <span
              className="sub clip"
              data-testid="issue-resolution-text"
              title={`${RESOLUTION_TEXT[issue.resolution]}${resolved?.note ? ` · ${resolved.note}` : ""}`}
            >
              {RESOLUTION_TEXT[issue.resolution]}
              {resolved?.note && ` · ${resolved.note}`}
            </span>
          )}
        </td>
        {showAccount && (
          <td data-label="Account" className="wrap">
            <a href={showAccount} onClick={go} data-testid="issue-account">
              {issue.account_number} {issue.account_name}
            </a>
          </td>
        )}
        <td data-label="Assignee" className="assignee" title={emailName(issue.assignee_email)}>
          {issue.mine ? "You" : emailName(issue.assignee_email)}
        </td>
        <td data-label="Opened" className="wrap" title={callTime(issue.opened_at, org.time_zone)}>
          {shortTime(issue.opened_at, org.time_zone)}
          <span className="sub below">
            {issue.status !== "closed" ? daysOpenText(days) : "Closed"}
            {issue.source === "call" && " · from a call"}
            {issue.source === "sensor" && " · from the sensor rules"}
          </span>
          {issue.customer_ack && (
            <span className="sub below" data-testid="issue-customer-ack" title={callTime(issue.customer_ack, org.time_zone)}>
              {customerAckText(issue.customer_ack, org.time_zone)}
            </span>
          )}
        </td>
        <td data-label="Due" className="due">{issue.due_date ? dayText(issue.due_date, true) : "–"}</td>
        <td data-label="" className="row-actions">
          {!step && steps.length > 0 && (
            <span className="actions">
              {steps.map((kind) => (
                <button key={kind} type="button" data-testid={`issue-${kind}`} onClick={() => setStep(kind)}>
                  {STEP_TEXT[kind]}
                </button>
              ))}
            </span>
          )}
        </td>
      </tr>
      {step && (
        <tr className="detail-row">
          <td colSpan={span} data-label="">
            <StepForm
              issue={issue}
              kind={step}
              auth={auth}
              user={user}
              onCancel={() => setStep(null)}
              onDone={(text) => {
                setStep(null);
                onChanged(text, step);
              }}
            />
          </td>
        </tr>
      )}
    </tbody>
  );
}

/** The issues as one dense table; the account column shows only outside an account page. */
export function IssueTable({
  issues,
  base,
  org,
  auth,
  user,
  param,
  fallback,
  onChanged,
}: {
  issues: api.Issue[];
  base: string | null;
  org: api.Bootstrap;
  auth: Auth;
  user: User;
  param: string;
  fallback: string;
  onChanged: (issue: api.Issue, text: string, kind: StepKind) => void;
}) {
  const columns = base ? ISSUE_COLUMNS : ISSUE_COLUMNS.filter((column) => column.key !== "account");
  const [sort, setSort] = useSort(param, columns);
  const span = columns.length + 1;
  return (
    <>
      <SortSelect id={`issue-${param}-select`} columns={columns} sort={sort} onSort={setSort} fallback={fallback} />
      <div className="table-scroll" tabIndex={0} role="region" aria-label="Issues">
        <table className="list issues-table" data-testid="issue-table" data-cols={`issue-${param}-select`}>
          <thead>
            <tr>
              {columns.map((column) => (
                <SortTh key={column.key} column={column} sort={sort} onSort={setSort} />
              ))}
              <th scope="col">
                <span className="visually-hidden">Steps</span>
              </th>
            </tr>
          </thead>
          {sortRows([...issues].sort(byUrgency), columns, sort).map((issue) => (
            <IssueCard
              key={issue.id}
              issue={issue}
              org={org}
              auth={auth}
              user={user}
              showAccount={base && `${base}/accounts/${issue.account_id}`}
              span={span}
              onChanged={(text, kind) => onChanged(issue, text, kind)}
            />
          ))}
        </table>
      </div>
    </>
  );
}

type IssueLoad = api.Issue[] | "server" | "network" | "session" | undefined;

function writeView(view: IssueView) {
  const url = new URL(window.location.href);
  if (view === "all") url.searchParams.set("view", "all");
  else url.searchParams.delete("view");
  window.history.replaceState(window.history.state, "", url);
}

export function IssueList({
  base,
  org,
  auth,
  user,
  canView,
  canTake,
}: {
  base: string;
  org: api.Bootstrap;
  auth: Auth;
  user: User;
  canView: boolean | undefined;
  canTake: boolean | undefined;
}) {
  const [view, setView] = useState<IssueView>(() => defaultView(window.location.search, canTake));
  const [closed, setClosed] = useState(false);
  const [found, setFound] = useState<IssueLoad>(undefined);
  const [attempt, setAttempt] = useState(0);
  const [note, setNote] = useState("");
  const [gone, setGone] = useState<api.Issue | null>(null);
  const status = useRef<HTMLParagraphElement>(null);
  const mine = view === "mine";
  useTitle(`Issues · ${org.name}`);
  useEffect(() => setView(defaultView(window.location.search, canTake)), [canTake]);
  useEffect(() => {
    if (canView !== true) return;
    let live = true;
    setFound(undefined);
    user
      .getIdToken()
      .then((token) => api.issues(token, mine, closed ? "closed" : "open"))
      .then((list) => live && setFound([...list].sort(byUrgency)))
      .catch((e) => {
        const code = e instanceof api.ApiError ? e.status : 0;
        if (live) setFound(code === 401 ? "session" : code >= 500 ? "server" : "network");
      });
    return () => {
      live = false;
    };
  }, [user, mine, closed, attempt, canView]);
  const say = (text: string, left: api.Issue | null) => {
    setNote(text);
    setGone(left);
    setAttempt((n) => n + 1);
    requestAnimationFrame(() => status.current?.focus());
  };
  const reopen = async (issue: api.Issue) => {
    try {
      await api.stepIssue(await user.getIdToken(), issue.id, { id: crypto.randomUUID(), kind: "reopen", note: "" });
      say(stepDoneText("reopen", issue), null);
    } catch (e) {
      const failure = saveFailure(e, "The step");
      setNote(failure.text);
    }
  };
  const pick = (next: IssueView) => {
    setView(next);
    writeView(next);
  };
  if (canView === false) {
    return (
      <main data-testid="issues-hidden">
        <h1 tabIndex={-1}>Issues</h1>
        <p>Your role does not show issues.</p>
      </main>
    );
  }
  return (
    <main className="issues-page wide" data-testid="issues-page">
      <h1 tabIndex={-1}>Issues</h1>
      <div className="tabs" role="group" aria-label="Which issues">
        <button type="button" aria-pressed={mine} data-testid="issues-mine" onClick={() => pick("mine")}>
          Assigned to me
        </button>
        <button type="button" aria-pressed={!mine} data-testid="issues-all" onClick={() => pick("all")}>
          All that I can see
        </button>
        <label className="check">
          <input type="checkbox" checked={closed} data-testid="issues-closed" onChange={(e) => setClosed(e.target.checked)} />
          Closed issues
        </label>
      </div>
      <p ref={status} tabIndex={-1} role="status" data-testid="issues-note">
        {note}
        {gone && (
          <>
            {" "}
            <button type="button" className="link" data-testid="issues-reopen" onClick={() => void reopen(gone)}>
              Reopen
            </button>
          </>
        )}
      </p>
      {found === undefined ? (
        <Skeleton shape="table" columns={6} rows={8} />
      ) : found === "session" ? (
        <p role="alert" className="error">
          Your session ended.{" "}
          <button type="button" onClick={() => signOut(auth)}>
            Sign in again
          </button>
        </p>
      ) : typeof found === "string" ? (
        <p role="alert" className="error">
          {found === "server" ? SERVER_PROBLEM : "Cannot reach the server. Check your connection."}{" "}
          <button type="button" onClick={() => setAttempt((n) => n + 1)}>
            Try again
          </button>
        </p>
      ) : found.length === 0 ? (
        <p data-testid="issues-none">
          {closed ? "No closed issues." : mine ? "No open issues are assigned to you." : "No open issues."}
          {mine && !closed && (
            <>
              {" "}
              <button type="button" className="link" data-testid="issues-see-all" onClick={() => pick("all")}>
                See all issues
              </button>
            </>
          )}
        </p>
      ) : (
        <IssueTable
          issues={found}
          base={base}
          org={org}
          auth={auth}
          user={user}
          param="sort"
          fallback="Most urgent first"
          onChanged={(issue, text, kind) => say(text, kind === "close" && !closed ? issue : null)}
        />
      )}
    </main>
  );
}
